import type { RenderTelemetryFrame } from "./renderTelemetry";
import { takeDebugTrace } from "./renderDebug";

export interface FrameProfileSample {
  totalMs: number;
  prepareMs: number;
  composeMs: number;
  overlayMs: number;
  dirtyTileCount: number;
  displayTileCount: number;
  liveItemCount: number;
  labelRasterCount: number;
  labelCacheHitCount: number;
  usedCameraMode: boolean;
  fps: number;
  telemetry: RenderTelemetryFrame;
}

interface ConsoleRow {
  step: string;
  ms: number;
  pct: string;
  detail: string;
}

export class FrameProfiler {
  private frameStart = 0;
  private prepareStart = 0;
  private composeStart = 0;
  private overlayStart = 0;
  private prepareMs = 0;
  private composeMs = 0;
  private overlayMs = 0;
  private dirtyTileCount = 0;
  private displayTileCount = 0;
  private liveItemCount = 0;
  private labelRasterCount = 0;
  private labelCacheHitCount = 0;
  private usedCameraMode = false;
  private lastFrameStart = 0;
  private readonly frameIntervals: number[] = [];
  private lastSample: FrameProfileSample = emptySample();
  private frameCounter = 0;

  constructor(
    public enabled = false,
    public showOverlay = false,
  ) {}

  beginFrame(usedCameraMode: boolean): void {
    if (!this.enabled) {
      return;
    }
    const now = performance.now();
    if (this.lastFrameStart > 0) {
      this.frameIntervals.push(now - this.lastFrameStart);
      if (this.frameIntervals.length > 120) {
        this.frameIntervals.shift();
      }
    }
    this.lastFrameStart = now;
    this.frameStart = now;
    this.prepareMs = 0;
    this.composeMs = 0;
    this.overlayMs = 0;
    this.dirtyTileCount = 0;
    this.displayTileCount = 0;
    this.liveItemCount = 0;
    this.labelRasterCount = 0;
    this.labelCacheHitCount = 0;
    this.usedCameraMode = usedCameraMode;
    this.frameCounter += 1;
  }

  beginPrepare(): void {
    if (!this.enabled) {
      return;
    }
    this.prepareStart = performance.now();
  }

  endPrepare(): void {
    if (!this.enabled) {
      return;
    }
    this.prepareMs = performance.now() - this.prepareStart;
  }

  beginCompose(): void {
    if (!this.enabled) {
      return;
    }
    this.composeStart = performance.now();
  }

  endCompose(): void {
    if (!this.enabled) {
      return;
    }
    this.composeMs = performance.now() - this.composeStart;
  }

  beginOverlay(): void {
    if (!this.enabled) {
      return;
    }
    this.overlayStart = performance.now();
  }

  endOverlay(): void {
    if (!this.enabled) {
      return;
    }
    this.overlayMs = performance.now() - this.overlayStart;
  }

  recordTiles(dirtyTileCount: number, displayTileCount: number): void {
    if (!this.enabled) {
      return;
    }
    this.dirtyTileCount = dirtyTileCount;
    this.displayTileCount = displayTileCount;
  }

  recordLiveItemCount(count: number): void {
    if (!this.enabled) {
      return;
    }
    this.liveItemCount = count;
  }

  recordLabelRaster(): void {
    if (!this.enabled) {
      return;
    }
    this.labelRasterCount += 1;
  }

  recordLabelCacheHit(): void {
    if (!this.enabled) {
      return;
    }
    this.labelCacheHitCount += 1;
  }

  endFrame(telemetry: RenderTelemetryFrame): FrameProfileSample {
    if (!this.enabled) {
      return this.lastSample;
    }

    const totalMs = performance.now() - this.frameStart;
    // Ignore multi-second gaps (idle between gesture bursts) so the overlay
    // fps does not collapse to ~1 after a pause.
    const recentIntervals = this.frameIntervals.filter(interval => interval < 250);
    const averageInterval = recentIntervals.length > 0
      ? recentIntervals.reduce((sum, interval) => sum + interval, 0) / recentIntervals.length
      : totalMs;
    const fps = averageInterval > 0 ? 1000 / averageInterval : 0;
    this.lastSample = {
      totalMs,
      prepareMs: this.prepareMs,
      composeMs: this.composeMs,
      overlayMs: this.overlayMs,
      dirtyTileCount: this.dirtyTileCount,
      displayTileCount: this.displayTileCount,
      liveItemCount: this.liveItemCount,
      labelRasterCount: this.labelRasterCount,
      labelCacheHitCount: this.labelCacheHitCount,
      usedCameraMode: this.usedCameraMode,
      fps,
      telemetry,
    };

    if (this.showOverlay) {
      this.queueConsoleLog(this.lastSample);
    }

    return this.lastSample;
  }

  getLastSample(): FrameProfileSample {
    return this.lastSample;
  }

  formatOverlayText(): string {
    const sample = this.lastSample;
    const t = sample.telemetry;
    return [
      `${sample.fps.toFixed(0)} fps  ${sample.totalMs.toFixed(1)}ms`,
      `prep ${sample.prepareMs.toFixed(1)}  comp ${sample.composeMs.toFixed(1)}  ovl ${sample.overlayMs.toFixed(1)}`,
      `tiles ${sample.dirtyTileCount} dirty / ${sample.displayTileCount} drawn  live ${sample.liveItemCount}`,
      `labels ${sample.labelCacheHitCount} hit / ${sample.labelRasterCount} raster  ${sample.usedCameraMode ? "camera" : "full"}`,
      `${t.trigger}  ${t.drawPath}  cache:${t.prepareCache}`,
    ].join("\n");
  }

  private queueConsoleLog(sample: FrameProfileSample): void {
    const inputSteps = takeDebugTrace();
    const rows = [
      ...inputSteps.map(step => ({
        step: `${step.phase}: ${step.step}`,
        ms: step.ms,
        pct: "",
        detail: step.detail,
      })),
      ...buildConsoleRows(sample),
    ];

    const inputMs = inputSteps.reduce((sum, step) => sum + step.ms, 0);
    const totalMs = sample.totalMs + inputMs;
    if (totalMs < 8) {
      return;
    }

    const t = sample.telemetry;
    const header = [
      `[render] frame #${this.frameCounter}`,
      `${sample.fps.toFixed(0)} fps`,
      `${sample.totalMs.toFixed(2)}ms draw`,
      inputMs > 0 ? `${inputMs.toFixed(2)}ms upstream` : undefined,
      `trigger=${t.trigger}`,
      `mode=${t.renderMode}`,
      `draw=${t.drawPath}`,
      `cache=${t.prepareCache}`,
    ].filter(Boolean).join(" · ");

    const log = totalMs >= 16 ? console.warn : console.groupCollapsed;
    log(header);
    console.table(rows);
    if (log === console.groupCollapsed) {
      console.groupEnd();
    }
  }
}

export function buildConsoleRows(sample: FrameProfileSample): ConsoleRow[] {
  const t = sample.telemetry;
  const total = Math.max(sample.totalMs, 0.001);

  const pct = (ms: number) => `${((ms / total) * 100).toFixed(1)}%`;

  return [
    {
      step: "1. controller wait",
      ms: round(t.controllerWaitMs),
      pct: pct(t.controllerWaitMs),
      detail: "schedule → flush (RAF coalesce)",
    },
    {
      step: "2. build overlay",
      ms: round(t.buildOverlayMs),
      pct: pct(t.buildOverlayMs),
      detail: "buildRenderOverlay()",
    },
    {
      step: "3. renderer wait",
      ms: round(t.rendererWaitMs),
      pct: pct(t.rendererWaitMs),
      detail: "scheduleDraw RAF",
    },
    {
      step: "4. tessellation",
      ms: round(t.tessellationMs),
      pct: pct(t.tessellationMs),
      detail: "documentToDisplayList()",
    },
    {
      step: "5. previews",
      ms: round(t.previewMs),
      pct: pct(t.previewMs),
      detail: "preview transforms + drafts",
    },
    {
      step: "6. classify",
      ms: round(t.classifyMs),
      pct: pct(t.classifyMs),
      detail: "splitItemsByRenderClass()",
    },
    {
      step: "7. prepare (total)",
      ms: round(sample.prepareMs),
      pct: pct(sample.prepareMs),
      detail: `cache=${t.prepareCache}`,
    },
    {
      step: "8. compose tiles",
      ms: round(t.composeTilesMs),
      pct: pct(t.composeTilesMs),
      detail: `${sample.dirtyTileCount} dirty / ${sample.displayTileCount} drawn`,
    },
    {
      step: "9. compose live",
      ms: round(t.composeLiveMs),
      pct: pct(t.composeLiveMs),
      detail: `${sample.liveItemCount} live items`,
    },
    {
      step: "10. compose backdrop",
      ms: round(t.composeBackdropMs),
      pct: pct(t.composeBackdropMs),
      detail: "background blur live pass",
    },
    {
      step: "11. compose (total)",
      ms: round(sample.composeMs),
      pct: pct(sample.composeMs),
      detail: "GPU scene pass",
    },
    {
      step: "12. overlays",
      ms: round(sample.overlayMs),
      pct: pct(sample.overlayMs),
      detail: `labels ${sample.labelCacheHitCount} hit / ${sample.labelRasterCount} raster`,
    },
    {
      step: "13. frame total",
      ms: round(sample.totalMs),
      pct: "100.0%",
      detail: `${sample.fps.toFixed(0)} fps`,
    },
  ];
}

function round(ms: number): number {
  return Math.round(ms * 100) / 100;
}

function emptySample(): FrameProfileSample {
  return {
    totalMs: 0,
    prepareMs: 0,
    composeMs: 0,
    overlayMs: 0,
    dirtyTileCount: 0,
    displayTileCount: 0,
    liveItemCount: 0,
    labelRasterCount: 0,
    labelCacheHitCount: 0,
    usedCameraMode: false,
    fps: 0,
    telemetry: {
      trigger: "unknown",
      drawPath: "scheduled",
      renderMode: "full",
      prepareCache: "fresh",
      controllerWaitMs: 0,
      rendererWaitMs: 0,
      buildOverlayMs: 0,
      tessellationMs: 0,
      previewMs: 0,
      classifyMs: 0,
      composeTilesMs: 0,
      composeLiveMs: 0,
      composeBackdropMs: 0,
    },
  };
}
